/**
 * SISTEMA DE PINNED THREADS (Single Source of Truth - SSOT)
 *
 * Qué es:
 * - Este servicio (ChatService) es la ÚNICA fuente de verdad para:
 *   - Snapshot de pinned (pinnedIds + pinnedOrder) — cache key 'chat_pinned_cache'.
 *   - Orden del thread list (pinned primero SIEMPRE).
 *   - Mutaciones de pin/unpin vía [TypeScript.togglePin()](frontend/src/services/chatService.ts:1076).
 * - La UI NO debe:
 *   - Pedir /instagram/pinned-threads directamente (excepto boot interno de este servicio).
 *   - Reordenar manualmente el listado de threads.
 *   - Forzar re-fetch de /threads tras pin/unpin.
 *   En su lugar, usar siempre este servicio + eventos (chatEvents).
 *
 * Cómo sincroniza (método/arquitectura):
 * 1) SSE (server‑driven):
 *    - El backend emite evento 'pinned' en pin/unpin.
 *      Este servicio escucha, actualiza el snapshot pinned y reordena el cache de threads.
 *      Ver listener en [TypeScript.SSEManager.connect()](frontend/src/services/chatService.ts:467).
 *    - Los eventos 'threads_updated' y 'message' también reescriben el cache de threads
 *      y pasan SIEMPRE por [TypeScript.reorderThreadsPinnedFirst()](frontend/src/services/chatService.ts:1290).
 *
 * 2) Cache SWR local:
 *    - Al boot, si no existe snapshot de pinned, hace UN GET a /instagram/pinned-threads para hidratar.
 *    - A partir de ahí, solo SSE mantiene la foto. TTL largo como fallback si no hay SSE.
 *
 * Orden estable del thread list (reglas):
 * - Prioridad:
 *   1) Pinned: por pinned_order ASC y, en empate, updated_time DESC.
 *   2) Unpinned: unread primero y luego updated_time DESC.
 * - Toda escritura al cache THREADS pasa por
 *   [TypeScript.reorderThreadsPinnedFirst(list)](frontend/src/services/chatService.ts:1290)
 *   (incluyendo SSE patches, markAsRead y boots mínimos).
 *
 * Reglas de uso (IMPORTANTE):
 * - No reordenes threads en componentes de UI.
 * - No llames /instagram/pinned-threads desde componentes; usa
 *   [TypeScript.togglePin()](frontend/src/services/chatService.ts:1076) y escucha 'pinned:updated'.
 * - No dispares refrescos completos de /threads tras pin/unpin; este servicio lo gestiona con SSE.
 *
 * Puntos de integración:
 * - Utilidad de ordenamiento:
 *   [TypeScript.reorderThreadsPinnedFirst()](frontend/src/services/chatService.ts:1290)
 * - Listener SSE para 'pinned':
 *   [TypeScript.SSEManager.connect()](frontend/src/services/chatService.ts:467)
 * - Mutación pin/unpin:
 *   [TypeScript.togglePin()](frontend/src/services/chatService.ts:1076)
 */
import api, { fastapi } from './api';
import { AuthenticatedEventStream } from './authenticatedEventStream';
import { demoStore, isDemoModeActive } from './demoModeService';
import type { Message } from '../contracts/chatContracts';
import { replyWindowEndsAtFromInbound } from '../utils/instagramReplyWindow';

export type { Message } from '../contracts/chatContracts';

// Simple EventEmitter implementation for browser
class EventEmitter {
  private events: Map<string, Set<(...args: any[]) => void>> = new Map();
  
  on(event: string, handler: (...args: any[]) => void): void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)!.add(handler);
  }
  
  off(event: string, handler: (...args: any[]) => void): void {
    this.events.get(event)?.delete(handler);
  }
  
  once(event: string, handler: (...args: any[]) => void): void {
    const onceHandler = (...args: any[]) => {
      handler(...args);
      this.off(event, onceHandler);
    };
    this.on(event, onceHandler);
  }
  
  emit(event: string, ...args: any[]): void {
    const handlers = this.events.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(...args);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }
  
  removeAllListeners(event?: string): void {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
    }
  }
  
  setMaxListeners(_n: number): void {
    // For compatibility, but not enforced in this simple implementation
  }
}
 
// Helper: ensure comercio_key in localStorage by decoding JWT (frontend-only)
function ensureComercioKeyFromJWT(): string | null {
  try {
    // Canonical storage key: "comercio_key"
    // Back-compat alias: "comercioKey" (will be kept in sync but never preferred)
    const alias = localStorage.getItem('comercioKey');
    const canonical = localStorage.getItem('comercio_key');
    const existing = canonical || alias;

    const token = localStorage.getItem('jwtToken');
    if (!token || token.split('.').length < 2) {
      return existing; // fallback to localStorage if no token
    }
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload || '{}');
    const ck = payload?.comercio_key || payload?.comercioKey || null;
    if (ck) {
      if (canonical !== ck) localStorage.setItem('comercio_key', ck);
      try { localStorage.removeItem('comercioKey'); } catch {}
      return ck;
    }
    
    return existing;
  } catch {
    return null;
  }
}

// Canonical getter/setter — RULE: always use comercio_key as source of truth
function getCanonicalComercioKey(): string | null {
  const canonical = localStorage.getItem('comercio_key');
  const alias = localStorage.getItem('comercioKey');
  if (canonical && canonical.trim() !== '') {
    // Enforce canonical-only: remove alias if exists
    if (alias) {
      try { localStorage.removeItem('comercioKey'); } catch {}
    }
    return canonical;
  }
  if (alias && alias.trim() !== '') {
    // Migrate alias to canonical and remove alias
    localStorage.setItem('comercio_key', alias);
    try { localStorage.removeItem('comercioKey'); } catch {}
    return alias;
  }
  return null;
}

// Auth readiness helper: wait until jwtToken and comercio_key exist before first authenticated calls
async function waitForAuthReady(maxMs: number = 2000): Promise<void> {
  const start = Date.now();
  try { ensureComercioKeyFromJWT(); } catch {}
  // Fast-path: if both already present, return immediately
  try {
    const t0 = typeof localStorage !== 'undefined' ? localStorage.getItem('jwtToken') : '';
    const ck0 = typeof localStorage !== 'undefined' ? localStorage.getItem('comercio_key') : '';
    if (t0 && t0.trim() !== '' && ck0 && ck0.trim() !== '') return;
  } catch {}
  // Otherwise poll for a short time (helps right after a full refresh)
  while (Date.now() - start < maxMs) {
    try {
      const t = typeof localStorage !== 'undefined' ? localStorage.getItem('jwtToken') : '';
      const ck = typeof localStorage !== 'undefined' ? localStorage.getItem('comercio_key') : '';
      if (t && t.trim() !== '' && ck && ck.trim() !== '') return;
    } catch {}
    await new Promise((r) => setTimeout(r, 50));
  }
}

// Types
export interface Thread {
  id: string;
  customer_id?: string;
  name: string;
  username?: string | null;
  lastMessage: string;
  unreadCount: number; // total unseen (client + bot)
  // Legacy boolean; kept for backward-compat in runtime, but prefer unreadCount
  unread?: boolean;
  updated_time: string;
  platform?: string | null;
  replyWindowEndsAt?: string | null;
  avatarUrl?: string | null;
  typing?: boolean;
  online?: boolean;

  // Added: per-kind unseen counters and last previews (not persisted; managed by ChatService cache)
  unreadClientCount?: number; // unseen messages from customer
  unreadBotCount?: number;    // unseen messages sent by bot/auto
  lastBotPreview?: string | null;
  lastClientPreview?: string | null;
  lastSenderKind?: 'human' | 'operator' | 'bot' | 'auto' | null;
  lastOrigin?: string | null;

  // Assigned tags for this thread (hydrated by bulk/tag events)
  tags?: Tag[];

  // Linked patient details
  patient?: any;
  
  // Indicates if the bot is currently enabled for this thread
  bot_enabled?: boolean | null;
  selected_bot_id?: number | null;
  appointment_booked?: boolean;
  escalated_to_human?: boolean;
  escalation_reason?: string | null;
}

// Tags model
export interface Tag {
  id: number;
  name: string;
  color?: string | null;
  folder_id?: number | null;
}

export interface TagFolder {
  id: number;
  name: string;
  parent_id?: number | null;
}

export interface ChatNotification {
  threadId: string;
  message: string;
  from: string;
  avatarUrl?: string;
  timestamp: string;
}

export interface ChatNote {
  id: number;
  thread_id: string;
  author_type: 'human' | 'bot';
  content: string;
  created_at: string;
}

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const THREADS_CACHE_KEY = 'chat_threads_cache';
const MESSAGES_CACHE_PREFIX = 'chat_messages_';
const MESSAGES_PAGE_META_PREFIX = 'chat_messages_page_meta_';
const PINNED_CACHE_KEY = 'chat_pinned_cache';

export const CHAT_MESSAGES_PAGE_SIZE = 10;

// Tags caches
const TAGS_CACHE_KEY = 'chat_tags_cache';
const TAG_FOLDERS_CACHE_KEY = 'chat_tag_folders_cache';
const THREAD_TAGS_PREFIX = 'chat_thread_tags_';

// Event emitter for real-time updates
class ChatEventEmitter extends EventEmitter {
  private static instance: ChatEventEmitter;
  
  private constructor() {
    super();
    this.setMaxListeners(50);
  }
  
  static getInstance(): ChatEventEmitter {
    if (!ChatEventEmitter.instance) {
      ChatEventEmitter.instance = new ChatEventEmitter();
    }
    return ChatEventEmitter.instance;
  }
}

// Cache manager
class CacheManager {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  
  set(key: string, data: any, ttl: number = CACHE_TTL): void {
    this.cache.set(key, {
      data: JSON.parse(JSON.stringify(data)), // Deep clone
      timestamp: Date.now() + ttl
    });
  }
  
  get(key: string): any | null {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() > item.timestamp) {
      this.cache.delete(key);
      return null;
    }
    
    return item.data;
  }
  
  invalidate(pattern?: string): void {
    if (!pattern) {
      this.cache.clear();
      return;
    }
    
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }
  
  has(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) return false;
    
    if (Date.now() > item.timestamp) {
      this.cache.delete(key);
      return false;
    }
    
    return true;
  }
}

// SSE Manager with auto-reconnect
class SSEManager {
  private eventSource: AuthenticatedEventStream | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private baseReconnectDelay = 1000;
  private isConnecting = false;
  private lastEventTime = Date.now();
  private heartbeatTimer: NodeJS.Timeout | null = null;
  // Guard per thread to avoid double bumps when 'message' and 'threads_updated' arrive for the same msg
  private unreadBumpGuard: Map<string, number> = new Map();
  private seenMessageIds: Set<string> = new Set();
  
  connect(): void {
    if (this.isConnecting || this.eventSource?.readyState === AuthenticatedEventStream.OPEN) {
      return;
    }
    
    this.isConnecting = true;
    // Re-sync comercio_key from JWT if needed
    ensureComercioKeyFromJWT();
    const comercioKey = getCanonicalComercioKey();
    const token = localStorage.getItem('jwtToken');
    
    // Require BOTH comercio_key and token because backend protects /api with authenticate()
    if (!comercioKey || !token) {
      this.isConnecting = false;
      return;
    }
    
    try {
      this.cleanup();
      this.isConnecting = true;
      this.eventSource = new AuthenticatedEventStream('/chat/sse/clients');

      this.eventSource.onopen = () => {
        console.log('[SSE] Connected');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.lastEventTime = Date.now();
        chatEvents.emit('sse:connected');
        this.startHeartbeat();
      };
      
      this.eventSource.onerror = (error) => {
        console.error('[SSE] Error:', error);
        this.isConnecting = false;
        chatEvents.emit('sse:disconnected');
        this.scheduleReconnect();
      };

      this.eventSource.addEventListener('access_revoked', (event) => {
        this.cleanup();
        try {
          const payload = JSON.parse((event as MessageEvent).data || '{}');
          if (payload.code === 'access_check_unavailable') return;
        } catch {}
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('comercioKey');
        localStorage.removeItem('comercio_key');
        localStorage.removeItem('timezone_iana');
        try { window.dispatchEvent(new Event('auth:logout')); } catch {}
        if (window.location.pathname !== '/access-pending') window.location.href = '/access-pending';
      });
      
      // Handle specific events
      this.eventSource.addEventListener('threads_updated', (event) => {
        this.lastEventTime = Date.now();
        try {
          const data = JSON.parse(event.data || '{}');
          const summary = data && typeof data === 'object' ? data.summary : null;
  
          if (summary && summary.id) {
            // Patch cached threads if available to avoid hitting /threads
            const cached = cache.get(THREADS_CACHE_KEY);
  
            const toNumber = (v: any) => {
              if (v == null) return 0;
              const n = Number(v);
              return isNaN(n) ? (v === true ? 1 : 0) : n;
            };
  
            if (Array.isArray(cached)) {
              const list = [...cached];
              const idx = list.findIndex((t: any) => String(t.id) === String(summary.id));
  
              if (idx >= 0) {
                const prev = list[idx];
                const prevUc = toNumber((prev as any).unreadCount ?? (prev as any).unread);
  
                let nextUc = prevUc;
                if (typeof (summary as any).unreadCount === 'number') {
                  nextUc = Math.max(0, (summary as any).unreadCount as number);
                } else if ((summary as any).unread === false) {
                  nextUc = 0;
                } else if ((summary as any).unread === true) {
                  // Accumulate when backend doesn't provide numeric count,
                  // but avoid double counting if we just bumped for this thread via 'message'
                  const lastBump = this.unreadBumpGuard.get(summary.id) || 0;
                  const tooRecent = Date.now() - lastBump < 1500;
                  nextUc = prevUc + (tooRecent ? 0 : 1);
                }
  
                list[idx] = { ...prev, ...summary, unreadCount: nextUc, unread: nextUc > 0 };
                // Stamp bump guard so successive summaries do not double increment
                this.unreadBumpGuard.set(summary.id, Date.now());
              } else {
                // Prepend if not present
                let baseUc = 0;
                if (typeof (summary as any).unreadCount === 'number') {
                  baseUc = Math.max(0, (summary as any).unreadCount as number);
                } else if ((summary as any).unread === true) {
                  baseUc = 1;
                }
  
                list.unshift({
                  id: summary.id,
                  customer_id: summary.customer_id,
                  name: summary.name || 'Cliente',
                  username: summary.username || null,
                  lastMessage: summary.lastMessage || '',
                  unreadCount: baseUc,
                  unread: baseUc > 0,
                  updated_time: summary.updated_time || new Date().toISOString(),
                  avatarUrl: summary.avatarUrl || null,
                  typing: false,
                  online: false,
                  patient: summary.patient || null,
                  platform: summary.platform || 'instagram',
                  replyWindowEndsAt: summary.replyWindowEndsAt || summary.reply_window_ends_at || null,
                });
                if (baseUc > 0) this.unreadBumpGuard.set(summary.id, Date.now());
              }
              cache.set(THREADS_CACHE_KEY, reorderThreadsPinnedFirst(list), CACHE_TTL);
              // Emit updated unread count so UI badges refresh without API calls
              const totalUnread = list.reduce((acc: number, t: any) => acc + toNumber((t as any).unreadCount ?? (t as any).unread), 0);
              chatEvents.emit('unread:count', totalUnread);
            } else {
              // Initialize a minimal threads cache from summary so totals can update immediately
              const list: any[] = [];
              let baseUc = 0;
              if (typeof (summary as any).unreadCount === 'number') {
                baseUc = Math.max(0, (summary as any).unreadCount as number);
              } else if ((summary as any).unread === true) {
                baseUc = 1;
              }
              list.unshift({
                id: summary.id,
                name: summary.name || 'Cliente',
                username: summary.username || null,
                lastMessage: summary.lastMessage || '',
                unreadCount: baseUc,
                unread: baseUc > 0,
                updated_time: summary.updated_time || new Date().toISOString(),
                avatarUrl: summary.avatarUrl || null,
                typing: false,
                online: false,
                platform: summary.platform || 'instagram',
                replyWindowEndsAt: summary.replyWindowEndsAt || summary.reply_window_ends_at || null,
              });
              if (baseUc > 0) this.unreadBumpGuard.set(summary.id, Date.now());
              cache.set(THREADS_CACHE_KEY, reorderThreadsPinnedFirst(list), CACHE_TTL);
              const toNumber2 = (v: any) => {
                if (v == null) return 0;
                const n = Number(v);
                return isNaN(n) ? (v === true ? 1 : 0) : n;
              };
              const totalUnread = list.reduce((acc: number, t: any) => acc + toNumber2((t as any).unreadCount ?? (t as any).unread), 0);
              chatEvents.emit('unread:count', totalUnread);
            }
          } else {
            // Generic update without summary — keep cache; 'message' events maintain unread counters
            // cache.invalidate(THREADS_CACHE_KEY);
          }
  
          // Notify UI with original payload
          chatEvents.emit('threads:updated', data);
        } catch (e) {
          console.error('[SSE] Error parsing threads_updated:', e);
        }
      });
      
      this.eventSource.addEventListener('message', (event) => {
        this.lastEventTime = Date.now();
        try {
          const data = JSON.parse(event.data || '{}');
          
          if (data.id) {
            if (this.seenMessageIds.has(data.id)) return;
            this.seenMessageIds.add(data.id);
          }

          const threadId = data.thread_id;
          if (threadId) {
            // Do NOT invalidate cached messages on every incoming event.
            // Keep cache to allow instant render on selection; mark thread "dirty" elsewhere.

            const cachedList = cache.get(THREADS_CACHE_KEY);
            const list = Array.isArray(cachedList) ? [...cachedList] : cachedList;

            // If message is not from me (customer inbound), accumulate client unseen locally
            if (data.from !== 'me') {
              if (Array.isArray(list)) {
                const idx = list.findIndex((t: any) => String(t.id) === String(threadId));
                if (idx >= 0) {
                  const prev = list[idx];
                  const lastMessage = data.preview || prev.lastMessage || '';
                  const updated_time = data.at || new Date().toISOString();
                  const replyWindowEndsAt = replyWindowEndsAtFromInbound(updated_time);
                  const prevClient = Number((prev as any).unreadClientCount || 0);
                  const prevBot = Number((prev as any).unreadBotCount || 0);
                  const nextClient = prevClient + 1;
                  const nextTotal = nextClient + prevBot;
                  list[idx] = {
                    ...prev,
                    unreadClientCount: nextClient,
                    unreadBotCount: prevBot,
                    unreadCount: nextTotal,
                    unread: nextTotal > 0,
                    lastMessage,
                    lastClientPreview: lastMessage,
                    lastSenderKind: String(data.sender_kind || 'human').toLowerCase(),
                    lastOrigin: data.origin || 'instagram',
                    updated_time,
                    replyWindowEndsAt,
                  };
                  cache.set(THREADS_CACHE_KEY, reorderThreadsPinnedFirst(list), CACHE_TTL);
                  // Stamp guard for this thread to avoid double count in immediate threads_updated
                  this.unreadBumpGuard.set(threadId, Date.now());

                  // Emit updated total unread (navbar) — sum of all unseen (client + bot)
                  const totalUnread = list.reduce((acc: number, t: any) => acc + Number((t as any).unreadCount || 0), 0);
                  chatEvents.emit('unread:count', totalUnread);

                  // Also emit a threads:updated summary for UI patchers
                  chatEvents.emit('threads:updated', { summary: { id: threadId, unreadCount: nextTotal, lastMessage, updated_time, replyWindowEndsAt } });
                } else {
                  const lastMessage = data.preview || '';
                  const updated_time = data.at || new Date().toISOString();
                  const replyWindowEndsAt = replyWindowEndsAtFromInbound(updated_time);
                  list.unshift({
                    id: threadId,
                    customer_id: data.customer_id || threadId,
                    name: 'Cliente',
                    username: null,
                    lastMessage,
                    unreadCount: 1,
                    unread: true,
                    updated_time,
                    avatarUrl: null,
                    typing: false,
                    online: false,
                    unreadClientCount: 1,
                    unreadBotCount: 0,
                    lastClientPreview: lastMessage,
                    lastBotPreview: null,
                    lastSenderKind: String(data.sender_kind || 'human').toLowerCase(),
                    lastOrigin: data.origin || 'instagram',
                    platform: 'instagram',
                    replyWindowEndsAt,
                  });
                  cache.set(THREADS_CACHE_KEY, reorderThreadsPinnedFirst(list), CACHE_TTL);
                  this.unreadBumpGuard.set(threadId, Date.now());
                  const totalUnread = list.reduce((acc: number, t: any) => acc + Number((t as any).unreadCount || 0), 0);
                  chatEvents.emit('unread:count', totalUnread);
                  chatEvents.emit('threads:updated', { summary: { id: threadId, unreadCount: 1, lastMessage, updated_time, replyWindowEndsAt } });
                }
              } else {
                // Initialize minimal thread cache so client totals can update even before /threads
                const lastMessage = data.preview || '';
                const updated_time = data.at || new Date().toISOString();
                const replyWindowEndsAt = replyWindowEndsAtFromInbound(updated_time);
                const newList: any[] = [{
                  id: threadId,
                  name: 'Cliente',
                  username: null,
                  lastMessage,
                  unreadCount: 1,
                  unread: true,
                  updated_time,
                  avatarUrl: null,
                  typing: false,
                  online: false,
                  unreadClientCount: 1,
                  unreadBotCount: 0,
                  lastClientPreview: lastMessage,
                  lastBotPreview: null,
                  lastSenderKind: String(data.sender_kind || 'human').toLowerCase(),
                  lastOrigin: data.origin || 'instagram',
                  platform: 'instagram',
                  replyWindowEndsAt,
                }];
                cache.set(THREADS_CACHE_KEY, reorderThreadsPinnedFirst(newList), CACHE_TTL);
                this.unreadBumpGuard.set(threadId, Date.now());
                chatEvents.emit('unread:count', 1);
                chatEvents.emit('threads:updated', { summary: { id: threadId, unreadCount: 1, lastMessage, updated_time, replyWindowEndsAt } });
              }
            }
          } else {
            console.debug('[SSE] message event without thread_id, will refresh threads');
          }
          // Patch thread lastMessage/updated_time even for outbound messages (from 'me'),
          // increment bot unseen for bot/auto, and optionally notify when it's bot/auto/n8n.
          try {
            if (data.from === 'me') {
              const cachedList2 = cache.get(THREADS_CACHE_KEY);
              const list = Array.isArray(cachedList2) ? [...cachedList2] : cachedList2;
              const lastMessage = data.preview || '';
              const updated_time = data.at || new Date().toISOString();

              const kind = String(data.sender_kind || '').toLowerCase();
              const origin = String(data.origin || '').toLowerCase();
              const isBotish = (kind === 'bot' || kind === 'auto' || origin === 'n8n');

              if (Array.isArray(list)) {
                const idx = list.findIndex((t: any) => String(t.id) === String(threadId));
                if (idx >= 0) {
                  const prev = list[idx];
                  const prevClient = Number((prev as any).unreadClientCount || 0);
                  const prevBot = Number((prev as any).unreadBotCount || 0);
                  const nextBot = isBotish ? (prevBot + 1) : prevBot;
                  const nextTotal = prevClient + nextBot;

                  list[idx] = {
                    ...prev,
                    lastMessage,
                    updated_time,
                    lastSenderKind: kind,
                    lastOrigin: data.origin || 'instagram',
                    unreadClientCount: prevClient,
                    unreadBotCount: nextBot,
                    unreadCount: nextTotal,
                    unread: nextTotal > 0,
                    lastBotPreview: isBotish ? lastMessage : (prev as any).lastBotPreview || null
                  };
                } else {
                  const nextBot = isBotish ? 1 : 0;
                  list.unshift({
                    id: threadId,
                    customer_id: data.customer_id || threadId,
                    name: 'Cliente',
                    username: null,
                    lastMessage,
                    unreadCount: nextBot,
                    unread: nextBot > 0,
                    updated_time,
                    avatarUrl: null,
                    typing: false,
                    online: false,
                    lastSenderKind: kind || 'operator',
                    lastOrigin: data.origin || 'instagram',
                    unreadClientCount: 0,
                    unreadBotCount: nextBot,
                    lastBotPreview: isBotish ? lastMessage : null,
                    lastClientPreview: null
                  });
                }
                cache.set(THREADS_CACHE_KEY, reorderThreadsPinnedFirst(list), CACHE_TTL);
                const nextRow = list.find((t: any) => String(t.id) === String(threadId));
                // Emit a threads summary so UI components can patch immediately
                chatEvents.emit('threads:updated', {
                  summary: {
                    id: threadId,
                    lastMessage,
                    updated_time,
                    unreadCount: Number(nextRow?.unreadCount || 0),
                    lastSenderKind: kind || 'operator',
                    lastOrigin: data.origin || 'instagram'
                  }
                });
                // Update navbar count (sum of all unseen)
                const totalUnread = list.reduce((acc: number, t: any) => acc + Number((t as any).unreadCount || 0), 0);
                chatEvents.emit('unread:count', totalUnread);
              }

              // Notify user when outbound is a bot/auto or came from n8n
              if (isBotish) {
                const cached = cache.get(THREADS_CACHE_KEY);
                const thread = Array.isArray(cached) ? cached.find((t: any) => String(t.id) === String(threadId)) : null;
                void notifications.notify({
                  threadId: threadId,
                  message: lastMessage || 'Nuevo mensaje del bot',
                  from: (thread?.name || 'Bot'),
                  avatarUrl: thread?.avatarUrl || undefined,
                  timestamp: updated_time
                });
              }
            }
          } catch (e) {
            console.error('[SSE] error handling outbound bot notification:', e);
          }
          // Emit an optimistic outbound bot/automation message into the open chat, so UI updates instantly.
          try {
            const selected = (chatService as any)?.getCurrentSelectedThreadId?.() || null;
            const kind2 = String(data.sender_kind || '').toLowerCase();
            const origin2 = String(data.origin || '').toLowerCase();
            const isBotish2 = (kind2 === 'bot' || kind2 === 'auto' || origin2 === 'n8n');
            if (data.from === 'me' && threadId && selected && String(threadId) === String(selected) && isBotish2) {
              const tempId = `sse_bot_${Date.now()}_${Math.random().toString(36).slice(2)}`;
              const optimisticMessage: Message = {
                id: tempId,
                tempId,
                fromMe: true,
                text: data.preview || '',
                imageUrl: undefined,
                createdAt: data.at || new Date().toISOString(),
                status: 'sent',
                senderKind: (data.sender_kind as any) || 'bot',
                origin: (data.origin as any) || 'taratic_api',
                botName: data.bot_name || 'bot'
              };
              chatEvents.emit('message:optimistic', { threadId, message: optimisticMessage });
            }
          } catch (e) {
            console.error('[SSE] error emitting optimistic bot message:', e);
          }
          chatEvents.emit('message:received', data);
        } catch (e) {
          console.error('[SSE] Error parsing message:', e);
        }
      });
      
      this.eventSource.addEventListener('typing', (event) => {
        this.lastEventTime = Date.now();
        try {
          const data = JSON.parse(event.data || '{}');
          chatEvents.emit('typing:status', data);
        } catch (e) {
          console.error('[SSE] Error parsing typing:', e);
        }
      });
      
      // Handle pinned updates to avoid refetch and reorder locally
      this.eventSource.addEventListener('pinned', (event) => {
        this.lastEventTime = Date.now();
        try {
          const data = JSON.parse(event.data || '{}');
          const threadId = String(data.thread_id || '');
          if (!threadId) return;

          // Update pinned cache snapshot
          const state = cache.get(PINNED_CACHE_KEY) || { pinnedIds: [], pinnedOrder: {} };
          const pinnedIds: string[] = Array.isArray(state.pinnedIds) ? [...state.pinnedIds] : [];
          const pinnedOrder: Record<string, number> =
            state && typeof state.pinnedOrder === 'object' && state.pinnedOrder ? { ...state.pinnedOrder } : {};
          if (data.is_pinned) {
            if (!pinnedIds.includes(threadId)) pinnedIds.push(threadId);
            pinnedOrder[threadId] = Number.isFinite(Number(data.pinned_order))
              ? Number(data.pinned_order)
              : (pinnedOrder[threadId] ?? 0);
          } else {
            const idx = pinnedIds.indexOf(threadId);
            if (idx >= 0) pinnedIds.splice(idx, 1);
            delete pinnedOrder[threadId];
          }
          // Keep a long TTL; SSE keeps it fresh
          cache.set(PINNED_CACHE_KEY, { pinnedIds, pinnedOrder }, 10 * 60 * 1000);

          // Reorder threads cache locally
          const list = cache.get(THREADS_CACHE_KEY);
          if (Array.isArray(list)) {
            const pinnedSet = new Set(pinnedIds);
            const sortByTime = (a: any, b: any) =>
              String(b.updated_time || '').localeCompare(String(a.updated_time || ''));
            const pinnedList = list
              .filter((t: any) => pinnedSet.has(t.id))
              .sort(
                (a: any, b: any) =>
                  (Number(pinnedOrder[a.id] || 0) - Number(pinnedOrder[b.id] || 0)) || sortByTime(a, b)
              );
            const unpinnedList = list.filter((t: any) => !pinnedSet.has(t.id));
            const sorted = [...pinnedList, ...unpinnedList];
            cache.set(THREADS_CACHE_KEY, sorted);
          }

          // Notify UI
          chatEvents.emit('pinned:updated', {
            threadId,
            isPinned: !!data.is_pinned,
            pinnedOrder,
          });
        } catch (e) {
          console.error('[SSE] Error parsing pinned:', e);
        }
      });

      this.eventSource.addEventListener('chat:thread-tags-updated', (event) => {
          this.lastEventTime = Date.now();
          try {
            const data = JSON.parse(event.data || '{}');
            chatEvents.emit('thread-tags:server', data);
          } catch (e) {
            console.error('[SSE] Error parsing chat:thread-tags-updated:', e);
          }
        });

        this.eventSource.addEventListener('chat:tags-updated', (event) => {
          this.lastEventTime = Date.now();
          try {
            const data = JSON.parse(event.data || '{}');
            chatEvents.emit('tags:server', data);
          } catch (e) {
            console.error('[SSE] Error parsing chat:tags-updated:', e);
          }
        });

        this.eventSource.addEventListener('hello', () => {
        this.lastEventTime = Date.now();
        console.log('[SSE] Hello received');
      });
      
    } catch (error) {
      console.error('[SSE] Connection error:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }
  
  private startHeartbeat(): void {
    // Relax heartbeat to avoid unnecessary reconnects when there is simply no activity.
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      const timeSinceLastEvent = Date.now() - this.lastEventTime;
      if (timeSinceLastEvent > 600000) { // 10 minutes without events
        console.log('[SSE] No events for 10min, reconnecting as safety...');
        this.reconnect();
      }
    }, 60000); // Check every 60 seconds
  }
  
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
  
  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[SSE] Max reconnection attempts reached');
      chatEvents.emit('sse:failed');
      return;
    }
    
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      30000
    );
    
    this.reconnectAttempts++;
    console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
  
  reconnect(): void {
    this.cleanup();
    this.reconnectAttempts = 0;
    this.connect();
  }
  
  disconnect(): void {
    this.cleanup();
    this.reconnectAttempts = 0;
  }
  
  private cleanup(): void {
    this.isConnecting = false;
    this.stopHeartbeat();
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
  
  isConnected(): boolean {
    return this.eventSource?.readyState === AuthenticatedEventStream.OPEN;
  }
}

// Notification manager
class NotificationManager {
  private permission: NotificationPermission = 'default';
  private soundEnabled = true;
  private notificationSound: HTMLAudioElement;
  
  constructor() {
    // Create notification sound
    this.notificationSound = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmFgU7k9n1unEiBC13yO/eizEIHWq+8+OWT');
    this.notificationSound.volume = 0.3;
    
    // Load preferences
    this.soundEnabled = localStorage.getItem('chat_sound_enabled') !== 'false';
    
    // Request permission on init
    this.requestPermission();
  }
  
  async requestPermission(): Promise<void> {
    if (!('Notification' in window)) return;
    
    if (Notification.permission === 'default') {
      this.permission = await Notification.requestPermission();
    } else {
      this.permission = Notification.permission;
    }
  }
  
  async notify(notification: ChatNotification): Promise<void> {
    // Play sound if enabled
    if (this.soundEnabled && document.hidden) {
      try {
        await this.notificationSound.play();
      } catch (e) {
        console.error('Failed to play notification sound:', e);
      }
    }
    
    // Show browser notification if permitted and page is hidden
    if (this.permission === 'granted' && document.hidden) {
      try {
        const notif = new Notification(notification.from, {
          body: notification.message,
          icon: notification.avatarUrl || '/vite.svg',
          tag: notification.threadId,
        });
        
        notif.onclick = () => {
          window.focus();
          chatEvents.emit('notification:clicked', notification.threadId);
          notif.close();
        };
        
        setTimeout(() => notif.close(), 5000);
      } catch (e) {
        console.error('Failed to show notification:', e);
      }
    }
    
    // Always emit event for badge updates
    chatEvents.emit('notification:new', notification);
  }
  
  setSoundEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
    localStorage.setItem('chat_sound_enabled', String(enabled));
  }
  
  isSoundEnabled(): boolean {
    return this.soundEnabled;
  }
}

// Request queue with debouncing
class RequestQueue {
  private queue: Map<string, {
    timeout: NodeJS.Timeout;
    fn: () => Promise<any>;
    promise: Promise<any>;
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
  }> = new Map();
  private inFlight: Map<string, Promise<any>> = new Map();

  private execute(key: string): void {
    const scheduled = this.queue.get(key);
    if (!scheduled) return;

    this.queue.delete(key);
    const request = (async () => scheduled.fn())();
    this.inFlight.set(key, request);

    request
      .then(scheduled.resolve, scheduled.reject)
      .finally(() => {
        // Do not remove a newer request that may have started for the same key.
        if (this.inFlight.get(key) === request) {
          this.inFlight.delete(key);
        }
      });
  }
  
  debounce(key: string, fn: () => Promise<any>, delay: number = 300): Promise<any> {
    // If a request is already in flight for this key, piggyback on it.
    const existingRequest = this.inFlight.get(key);
    if (existingRequest) return existingRequest;

    // Keep true debounce semantics while sharing one promise between every
    // caller. Previously, clearing this timeout orphaned the earlier promise.
    const scheduled = this.queue.get(key);
    if (scheduled) {
      clearTimeout(scheduled.timeout);
      scheduled.fn = fn;
      scheduled.timeout = setTimeout(() => this.execute(key), delay);
      return scheduled.promise;
    }

    let resolveRequest!: (value: any) => void;
    let rejectRequest!: (reason?: any) => void;
    const promise = new Promise<any>((resolve, reject) => {
      resolveRequest = resolve;
      rejectRequest = reject;
    });

    const timeout = setTimeout(() => this.execute(key), delay);
    this.queue.set(key, {
      timeout,
      fn,
      promise,
      resolve: resolveRequest,
      reject: rejectRequest,
    });
    return promise;
  }
  
  isInFlight(key: string): boolean {
    return this.inFlight.has(key);
  }
  
  cancel(key: string): void {
    const scheduled = this.queue.get(key);
    if (scheduled) {
      clearTimeout(scheduled.timeout);
      this.queue.delete(key);
      // Settle callers instead of leaving them waiting forever.
      scheduled.resolve(undefined);
    }
  }
}

export interface ThreadsPage {
  threads: Thread[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface MessagesPage {
  messages: Message[];
  nextCursor: string | null;
  hasMore: boolean;
}

function mergeMessagesChronologically(existing: Message[], incoming: Message[]): Message[] {
  const byId = new Map<string, Message>();
  for (const message of [...existing, ...incoming]) {
    const previous = byId.get(message.id);
    byId.set(message.id, previous ? { ...previous, ...message } : message);
  }

  return Array.from(byId.values()).sort((a, b) => {
    const left = Date.parse(a.createdAt || '');
    const right = Date.parse(b.createdAt || '');
    if (!Number.isFinite(left) || !Number.isFinite(right)) return 0;
    return left - right;
  });
}

// Singleton instances
const cache = new CacheManager();
const sse = new SSEManager();
const notifications = new NotificationManager();
const requestQueue = new RequestQueue();
const chatEvents = ChatEventEmitter.getInstance();

// Main Chat Service
class ChatService {
  private unreadCount = 0;
  private typingTimers: Map<string, NodeJS.Timeout> = new Map();
  // Track threads that received new content since last open to force refresh on selection
  private dirtyThreads: Set<string> = new Set();
  // Per-thread freshness to avoid re-fetching on quick thread switches (Instagram-like behavior)
  private lastFetchedAt: Map<string, number> = new Map();
  // Current selected thread id (for SSE optimistic insertion decisions)
  private currentSelectedThreadId: string | null = null;
  
  constructor() {
    this.setupEventListeners();
    // Fallback polling hooks: if SSE drops or never connects, keep UI fresh
    chatEvents.on('sse:connected', () => this.stopPolling());
    chatEvents.on('sse:disconnected', () => this.startPolling());
    chatEvents.on('sse:failed', () => this.startPolling());
  }
  
  private setupEventListeners(): void {
    // Handle new messages
    chatEvents.on('message:received', async (data) => {
      const { thread_id, preview, from, at } = data;
      
      // Mark thread as dirty so selecting it forces a reload
      if (thread_id) this.dirtyThreads.add(thread_id);
      
      // Avoid hitting /threads here; rely on cached data and the upcoming threads_updated summary
      const cached = cache.get(THREADS_CACHE_KEY);
      const thread = Array.isArray(cached) ? cached.find((t: any) => t.id === thread_id) : null;
      
      if (from !== 'me') {
        await notifications.notify({
          threadId: thread_id,
          message: preview || 'Nuevo mensaje',
          from: thread?.name || 'Instagram',
          avatarUrl: thread?.avatarUrl || undefined,
          timestamp: at
        });
      }
      // Do not recompute unread count here. The 'threads_updated' handler will patch cache and emit unread:count.
    });
    
    // Tags/folders SSE patches
    chatEvents.on('tags:server', (payload: any) => {
      try {
        // Any structural change invalidates both tags and folders caches
        cache.invalidate(TAGS_CACHE_KEY);
        cache.invalidate(TAG_FOLDERS_CACHE_KEY);
        chatEvents.emit('tags:updated', payload);
      } catch (e) {
        console.error('[ChatService] error handling tags:server:', e);
      }
    });

    chatEvents.on('thread-tags:server', (payload: any) => {
      try {
        const threadId = String(payload?.thread_id || '');
        if (!threadId) return;
        const tags = Array.isArray(payload?.tags) ? payload.tags : null;
        // Cache current thread tags if provided
        if (tags) {
          cache.set(`${THREAD_TAGS_PREFIX}${threadId}`, tags, 5 * 60 * 1000);
        } else {
          // Invalidate mapping for that thread
          cache.invalidate(`${THREAD_TAGS_PREFIX}${threadId}`);
        }
        chatEvents.emit('thread:tags', { threadId, tagIds: Array.isArray(payload?.tag_ids) ? payload.tag_ids : undefined, tags });
      } catch (e) {
        console.error('[ChatService] error handling thread-tags:server:', e);
      }
    });

    // Handle typing status
    chatEvents.on('typing:status', (data) => {
      const { thread_id, is_typing } = data;
      
      // Clear existing timer
      const existingTimer = this.typingTimers.get(thread_id);
      if (existingTimer) {
        clearTimeout(existingTimer);
        this.typingTimers.delete(thread_id);
      }
      
      if (is_typing) {
        // Set typing indicator
        chatEvents.emit('thread:typing', { threadId: thread_id, typing: true });
        
        // Auto-clear after 3 seconds
        const timer = setTimeout(() => {
          chatEvents.emit('thread:typing', { threadId: thread_id, typing: false });
          this.typingTimers.delete(thread_id);
        }, 3000);
        
        this.typingTimers.set(thread_id, timer);
      } else {
        chatEvents.emit('thread:typing', { threadId: thread_id, typing: false });
      }
    });
  }
  
  // Initialize service
  async initialize(): Promise<void> {
    // Do not attempt to initialize chat/SSE before login
    const token = localStorage.getItem('jwtToken');
    const ckBefore = getCanonicalComercioKey() || ensureComercioKeyFromJWT();
    if (!token || !ckBefore) {
      // Not logged in yet, skip initialization silently
      return;
    }

    if (isDemoModeActive()) {
      sse.disconnect();
      const data = demoStore.mutate(() => {});
      const threads = reorderThreadsPinnedFirst(data.threads);
      cache.set(PINNED_CACHE_KEY, { pinnedIds: data.pinnedIds, pinnedOrder: Object.fromEntries(data.pinnedIds.map((id, i) => [id, i])) });
      cache.set(THREADS_CACHE_KEY, threads);
      this.unreadCount = threads.reduce((sum, thread) => sum + Number(thread.unreadCount || 0), 0);
      chatEvents.emit('threads:updated', threads);
      chatEvents.emit('unread:count', this.unreadCount);
      return;
    }

    // Credentials already identify the tenant; legacy connection metadata must
    // not block opening the event stream or loading the first snapshot.
    sse.connect();
    // Start polling if SSE is not yet connected (will auto-stop when connected)
    if (!sse.isConnected()) this.startPolling();
  }
  
  // Cleanup
  destroy(): void {
    sse.disconnect();
    this.stopPolling();
    cache.invalidate();
    for (const timer of this.typingTimers.values()) {
      clearTimeout(timer);
    }
    this.typingTimers.clear();
  }
  
  // Get connection status
  async getConnectionStatus(): Promise<any> {
    if (isDemoModeActive()) return { connected: true, comercio_key: getCanonicalComercioKey(), demo: true };
    // Only query connection status AFTER login (requires jwt + comercio_key)
    const token = localStorage.getItem('jwtToken');
    const comercioKey = getCanonicalComercioKey();
    if (!token || !comercioKey) {
      return { connected: false };
    }

    const cacheKey = `connection_status_${comercioKey}`;
    const cached = cache.get(cacheKey);
    if (cached) return cached;
    
    try {
      const params = `?comercio_key=${encodeURIComponent(comercioKey)}`;
      const { data } = await api.get(`/instagram/connection-status${params}`, { timeout: 15_000 });
      
      cache.set(cacheKey, data, 60000); // Cache for 1 minute
      return data;
    } catch (error) {
      console.error('Error fetching connection status:', error);
      // Do not throw during boot; return disconnected to avoid login-screen errors
      return { connected: false };
    }
  }
  
  // --- API Methods ---
  public async updateBotStatus(threadId: string, botEnabled: boolean): Promise<void> {
    if (isDemoModeActive()) {
      const data = demoStore.mutate(state => {
        const thread = state.threads.find(item => item.id === threadId);
        if (thread) { thread.bot_enabled = botEnabled; thread.escalated_to_human = false; thread.escalation_reason = null; }
      });
      cache.set(THREADS_CACHE_KEY, reorderThreadsPinnedFirst(data.threads));
      chatEvents.emit('threads:updated', data.threads);
      return;
    }
    try {
      await fastapi.patch(`/chat/sessions/${threadId}/bot_status`, { bot_enabled: botEnabled });
      
      // Optimistically update the thread locally
      const threads = cache.get(THREADS_CACHE_KEY);
      if (Array.isArray(threads)) {
        const thread = threads.find(t => t.id === threadId);
        if (thread) {
          thread.bot_enabled = botEnabled;
          // The bot switch controls availability only. A human intervention is
          // created by the explicit escalation flow, never by switching BOT OFF.
          thread.escalated_to_human = false;
          thread.escalation_reason = null;
          cache.set(THREADS_CACHE_KEY, threads);
          chatEvents.emit('threads:updated', threads);
        }
      }
    } catch (e) {
      console.error('Error updating bot status', e);
      throw e;
    }
  }

  // First-page compatibility wrapper. Consumers needing pagination should use getThreadsPage.
  async getThreads(forceRefresh = false): Promise<Thread[]> {
    return (await this.getThreadsPage({ forceRefresh })).threads;
  }

  // Fetch a bounded page of conversations. Cursor is opaque and is supplied by the API.
  async getThreadsPage(options: { cursor?: string | null; limit?: number; forceRefresh?: boolean } = {}): Promise<ThreadsPage> {
    const cursor = options.cursor || null;
    const limit = Math.min(50, Math.max(1, Number(options.limit || 50)));
    const forceRefresh = !!options.forceRefresh;
    if (isDemoModeActive()) {
      const data = demoStore.mutate(() => {});
      const threads = reorderThreadsPinnedFirst(data.threads.map(thread => ({ ...thread, tags: demoStore.threadTags(thread.id) })));
      cache.set(PINNED_CACHE_KEY, { pinnedIds: data.pinnedIds, pinnedOrder: Object.fromEntries(data.pinnedIds.map((id, i) => [id, i])) });
      cache.set(THREADS_CACHE_KEY, threads);
      return { threads, nextCursor: null, hasMore: false };
    }
    if (!cursor && !forceRefresh && cache.has(THREADS_CACHE_KEY)) {
      const cached = cache.get(THREADS_CACHE_KEY);
      return { threads: Array.isArray(cached) ? cached : [], nextCursor: null, hasMore: false };
    }
    
    const result = await requestQueue.debounce(`threads:${cursor || 'first'}`, async () => {
      // Avoid network calls before login; if auth not ready, return cache (or empty) without hitting API
      try {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('jwtToken') : '';
        const ckRuntime = getCanonicalComercioKey() || ensureComercioKeyFromJWT();
        if (!token || !ckRuntime) {
          const cachedOnly = cache.get(THREADS_CACHE_KEY);
          return { threads: Array.isArray(cachedOnly) ? cachedOnly : [], nextCursor: null, hasMore: false };
        }
      } catch {}
      try {
        // Get pinned threads (prefer cache; SSE keeps it fresh). Fetch only if cache is empty.
        let pinnedIds: string[] = [];
        let pinnedOrder: Record<string, number> = {};
        const pinnedState = cache.get(PINNED_CACHE_KEY);
        if (pinnedState && Array.isArray(pinnedState.pinnedIds)) {
          pinnedIds = [...pinnedState.pinnedIds];
          pinnedOrder = { ...(pinnedState.pinnedOrder || {}) };
        } else {
          try {
            const ck = getCanonicalComercioKey();
            const token = typeof localStorage !== 'undefined' ? localStorage.getItem('jwtToken') : '';
            if (ck && token) {
              const ckParam = `?comercio_key=${encodeURIComponent(ck)}`;
              const pinnedUrl = `/chat/pinned-threads${ckParam}&ts=${Date.now()}`;
              const pinnedRes = await fastapi.get(pinnedUrl, { timeout: 10_000 });
              const pinnedData = Array.isArray(pinnedRes.data) ? pinnedRes.data : (pinnedRes.data?.data || []);
              pinnedIds = pinnedData.map((p: any) => p.thread_id);
              pinnedData.forEach((p: any) => {
                pinnedOrder[p.thread_id] = p.pinned_order ?? 0;
              });
              cache.set(PINNED_CACHE_KEY, { pinnedIds, pinnedOrder }, 10 * 60 * 1000);
            } else {
              // Not authenticated yet — skip fetching pinned snapshot
            }
          } catch (e) {
            console.debug('Pinned threads not available:', e);
          }
        }
        
        // Get threads
        const ck2 = getCanonicalComercioKey();
        const params = new URLSearchParams({ limit: String(limit), ts: String(Date.now()) });
        if (ck2) params.set('comercio_key', ck2);
        if (cursor) params.set('cursor', cursor);
        const threadsUrl = `/chat/threads/page?${params.toString()}`;
        const { data } = await fastapi.get(threadsUrl, { timeout: 30_000 });
        const raw = Array.isArray(data) ? data : (data?.threads || data?.data || []);
        const nextCursor = Array.isArray(data) ? null : (data?.next_cursor ?? data?.nextCursor ?? null);
        const hasMore = Array.isArray(data) ? false : Boolean(data?.has_more ?? data?.hasMore ?? nextCursor);
        
        // Ensure raw is an array
        if (!Array.isArray(raw)) {
          console.warn('Invalid threads data from API:', raw);
          if (!cursor) cache.set(THREADS_CACHE_KEY, []);
          return { threads: Array.isArray(cache.get(THREADS_CACHE_KEY)) ? cache.get(THREADS_CACHE_KEY) : [], nextCursor: null, hasMore: false };
        }
        
        // Preserve any higher local unread counters from cache (accumulated via SSE)
        const prevList = cache.get(THREADS_CACHE_KEY);
        const prevMap = new Map<string, number>();
        const prevClientMap = new Map<string, number>();
        const prevBotMap = new Map<string, number>();
        const prevLastBotPreview = new Map<string, string>();
        const prevLastClientPreview = new Map<string, string>();
        const prevReplyWindowEndsAt = new Map<string, string>();
        const toNumber = (v: any) => {
          if (v == null) return 0;
          const n = Number(v);
          return isNaN(n) ? (v === true ? 1 : 0) : n;
        };
        if (Array.isArray(prevList)) {
          for (const t of prevList) {
            prevMap.set(String(t.id), toNumber((t as any).unreadCount ?? (t as any).unread));
            prevClientMap.set(String(t.id), Number((t as any).unreadClientCount || 0));
            prevBotMap.set(String(t.id), Number((t as any).unreadBotCount || 0));
            if ((t as any).lastBotPreview) prevLastBotPreview.set(String(t.id), String((t as any).lastBotPreview));
            if ((t as any).lastClientPreview) prevLastClientPreview.set(String(t.id), String((t as any).lastClientPreview));
            if ((t as any).replyWindowEndsAt) prevReplyWindowEndsAt.set(String(t.id), String((t as any).replyWindowEndsAt));
          }
        }

        const pageThreads: Thread[] = raw.map((t: any) => {
          // Enriched counts and previews provided by backend on refresh
          const rawClient = Number((t as any).unreadClientCount || (t as any).unread_client_count || 0);
          const rawBot = Number((t as any).unreadBotCount || (t as any).unread_bot_count || 0);
          const apiUc = Number((t as any).unreadCount ?? (t as any).unread ?? 0);
          const prevUc = prevMap.get(String(t.id)) ?? 0;
          const prevClient = prevClientMap.get(String(t.id)) ?? 0;
          const prevBot = prevBotMap.get(String(t.id)) ?? 0;

          // Choose the maximum to avoid losing unseen state due to timing
          const unreadClient = Math.max(rawClient, prevClient);
          const unreadBot = Math.max(rawBot, prevBot);
          const unreadCount = Math.max(apiUc, prevUc, unreadClient + unreadBot);

          const displayName = (t.name || '') as string;
          const useFallback = /^\d+$/.test(displayName) && !t.username;

          // Prefer backend previews if present, fallback to previous cache
          const lastBotPrev = (t as any).lastBotPreview || (t as any).last_bot_preview || prevLastBotPreview.get(String(t.id)) || null;
          const lastClientPrev = (t as any).lastClientPreview || (t as any).last_client_preview || prevLastClientPreview.get(String(t.id)) || null;

          // Base lastMessage from API, but if there is unread bot, prefer bot preview to match UI expectation
          const baseLast = (t.lastMessage || '') as string;
          const finalLast = unreadBot > 0 && lastBotPrev ? String(lastBotPrev) : baseLast;

          return {
            id: t.id,
            customer_id: t.customer_id,
            name: useFallback ? `Cliente ${String(displayName).slice(-4)}` : (displayName || 'Cliente'),
            username: t.username || null,
            lastMessage: finalLast,
            unreadCount,
            unread: unreadCount > 0,
            updated_time: t.updated_time,
            platform: t.platform || 'instagram',
            replyWindowEndsAt: (t as any).replyWindowEndsAt || (t as any).reply_window_ends_at || prevReplyWindowEndsAt.get(String(t.id)) || null,
            avatarUrl: t.avatarUrl || null,
            patient: t.patient || null,
            typing: false,
            online: false,
            // per-kind unseen and previews
            unreadClientCount: unreadClient,
            unreadBotCount: unreadBot,
            lastBotPreview: lastBotPrev,
            lastClientPreview: lastClientPrev,
            lastSenderKind: (t as any).last_sender_kind || null,
            lastOrigin: (t as any).last_origin || null,
            bot_enabled: t.bot_enabled,
            selected_bot_id: t.selected_bot_id ?? null,
            appointment_booked: Boolean(t.appointment_booked),
            escalated_to_human: Boolean(t.escalated_to_human),
            escalation_reason: t.escalation_reason || null
          } as Thread;
        });
        
        // Sort threads
        const sortByTime = (a: Thread, b: Thread) =>
          (b.updated_time || '').localeCompare(a.updated_time || '');
        const sortUnpinned = (a: Thread, b: Thread) => {
          const au = Number((((a as any).unreadCount ?? ((a as any).unread ? 1 : 0)) as number) > 0);
          const bu = Number((((b as any).unreadCount ?? ((b as any).unread ? 1 : 0)) as number) > 0);
          return (bu - au) || sortByTime(a, b);
        };
        
        // Keep prior pages and real-time updates while adding the new page. A refresh of
        // page one intentionally replaces the snapshot so removed conversations disappear.
        const threads = cursor
          ? [...(Array.isArray(prevList) ? prevList : []), ...pageThreads].filter(
              (thread, index, list) => list.findIndex((candidate) => String(candidate.id) === String(thread.id)) === index
            )
          : pageThreads;

        const pinned = threads
          .filter(t => pinnedIds.includes(t.id))
          .sort((a, b) => (pinnedOrder[a.id] || 0) - (pinnedOrder[b.id] || 0) || sortByTime(a, b));
        
        const unpinned = threads
          .filter(t => !pinnedIds.includes(t.id))
          .sort(sortUnpinned);
        
        const sortedThreads = [...pinned, ...unpinned];
        
        cache.set(THREADS_CACHE_KEY, sortedThreads);
        this.updateUnreadCount(sortedThreads);
        return { threads: sortedThreads, nextCursor: nextCursor ? String(nextCursor) : null, hasMore };
      } catch (error) {
        console.error('Error fetching threads:', error);
        // Preserve a prior page on transient errors; do not make the list disappear.
        const cached = cache.get(THREADS_CACHE_KEY);
        if (!Array.isArray(cached)) throw error;
        return { threads: cached, nextCursor: cursor, hasMore: !!cursor };
      }
    });
  
    // If debounce returned null because a request is in-flight, fall back to cache
    if (result == null) {
      const cached = cache.get(THREADS_CACHE_KEY);
      return { threads: Array.isArray(cached) ? cached : [], nextCursor: cursor, hasMore: !!cursor };
    }
    return result;
  }
  
  // Get messages with caching
  async getMessages(threadId: string, forceRefresh = false, beforeTs?: string): Promise<Message[]> {
    if (!threadId) return [];

    const ck = getCanonicalComercioKey();
    if (!ck) return [];

    const cacheKey = `${MESSAGES_CACHE_PREFIX}${threadId}`;
    if (!forceRefresh && !beforeTs) {
      const cached = cache.get(cacheKey);
      if (cached) return cached;
    }

    // Keep the timestamp endpoint available for older callers while the chat UI
    // uses the stable keyset endpoint below.
    if (beforeTs) {
      const queryParams = new URLSearchParams({ comercio_key: ck, before_ts: beforeTs });
      const { data } = await api.get(
        `/v1/chat/threads/${encodeURIComponent(threadId)}/messages?${queryParams.toString()}`
      );
      return Array.isArray(data) ? data : (data?.data || []);
    }

    const page = await this.getMessagesPage(threadId);
    const cached = cache.get(cacheKey);
    return Array.isArray(cached) ? cached : page.messages;
  }
  
  // Get one 10-message keyset page. Older pages are merged into the same cache
  // so switching threads does not discard history the operator already loaded.
  async getMessagesPage(threadId: string, cursor?: string): Promise<MessagesPage> {
    if (!threadId) return { messages: [], nextCursor: null, hasMore: false };

    if (isDemoModeActive()) {
      const messages = demoStore.messages(threadId);
      cache.set(`${MESSAGES_CACHE_PREFIX}${threadId}`, messages);
      cache.set(`${MESSAGES_PAGE_META_PREFIX}${threadId}`, { nextCursor: null, hasMore: false });
      this.lastFetchedAt.set(threadId, Date.now());
      this.clearThreadDirty(threadId);
      return { messages, nextCursor: null, hasMore: false };
    }

    const ck = getCanonicalComercioKey();
    if (!ck) return { messages: [], nextCursor: null, hasMore: false };

    const requestKey = `${MESSAGES_CACHE_PREFIX}${threadId}_page_${cursor || 'first'}`;
    const result = await requestQueue.debounce(requestKey, async () => {
      const queryParams = new URLSearchParams({
        comercio_key: ck,
        limit: String(CHAT_MESSAGES_PAGE_SIZE),
        ts: String(Date.now()),
      });
      if (cursor) queryParams.set('cursor', cursor);

      const { data } = await api.get(
        `/v1/chat/threads/${encodeURIComponent(threadId)}/messages/page?${queryParams.toString()}`
      );
      const page: MessagesPage = {
        messages: Array.isArray(data?.messages) ? data.messages : [],
        nextCursor: data?.next_cursor ? String(data.next_cursor) : null,
        hasMore: !!data?.has_more,
      };

      const messagesKey = `${MESSAGES_CACHE_PREFIX}${threadId}`;
      const metaKey = `${MESSAGES_PAGE_META_PREFIX}${threadId}`;
      const currentMessages = cache.get(messagesKey);
      const currentMeta = cache.get(metaKey) as Omit<MessagesPage, 'messages'> | null;

      if (cursor) {
        const combined = mergeMessagesChronologically(
          page.messages,
          Array.isArray(currentMessages) ? currentMessages : []
        );
        cache.set(messagesKey, combined);
        cache.set(metaKey, { nextCursor: page.nextCursor, hasMore: page.hasMore });
      } else {
        const existing = Array.isArray(currentMessages) ? currentMessages : [];
        const incomingIds = new Set(page.messages.map((message) => message.id));
        const overlaps = existing.some((message: Message) => incomingIds.has(message.id));
        const retainLoadedHistory = existing.length > CHAT_MESSAGES_PAGE_SIZE && overlaps;

        cache.set(
          messagesKey,
          retainLoadedHistory
            ? mergeMessagesChronologically(existing, page.messages)
            : page.messages
        );
        cache.set(
          metaKey,
          retainLoadedHistory && currentMeta
            ? currentMeta
            : { nextCursor: page.nextCursor, hasMore: page.hasMore }
        );

        this.lastFetchedAt.set(threadId, Date.now());
        this.clearThreadDirty(threadId);
      }

      return page;
    }, 0);

    return result || this.getMessagesCachedPage(threadId);
  }

  // --- NOTES API ---
  async getNotes(threadId: string): Promise<ChatNote[]> {
    if (!threadId) return [];
    if (isDemoModeActive()) return demoStore.notes(threadId);
    try {
      const { data } = await api.get(`/v1/chat/threads/${encodeURIComponent(threadId)}/notes`);
      return data;
    } catch (err) {
      console.error('Error fetching notes:', err);
      return [];
    }
  }

  async createNote(threadId: string, content: string): Promise<ChatNote> {
    if (isDemoModeActive()) {
      let note!: ChatNote;
      demoStore.mutate(data => {
        note = { id: demoStore.nextId(data), thread_id: threadId, author_type: 'human', content, created_at: new Date().toISOString() };
        (data.notes[threadId] ||= []).push(note);
      });
      return note;
    }
    const { data } = await api.post(`/v1/chat/threads/${encodeURIComponent(threadId)}/notes`, { content });
    return data;
  }

  async updateNote(noteId: number, content: string): Promise<ChatNote> {
    if (isDemoModeActive()) {
      let found!: ChatNote;
      demoStore.mutate(data => {
        for (const notes of Object.values(data.notes)) {
          const note = notes.find(item => item.id === noteId);
          if (note) { note.content = content; found = note; break; }
        }
      });
      if (!found) throw new Error('Demo note not found');
      return found;
    }
    const { data } = await api.put(`/v1/chat/threads/notes/${noteId}`, { content });
    return data;
  }

  async deleteNote(noteId: number): Promise<void> {
    if (isDemoModeActive()) {
      demoStore.mutate(data => { for (const key of Object.keys(data.notes)) data.notes[key] = data.notes[key].filter(note => note.id !== noteId); });
      return;
    }
    await api.delete(`/v1/chat/threads/notes/${noteId}`);
  }
  // -----------------

  
  // Send message with optimistic update
  async sendMessage(
    threadId: string,
    content: { text?: string; imageUrl?: string; mediaUrl?: string; mediaType?: string; file?: File },
    options: { senderKind?: string; origin?: string; botName?: string } = {}
  ): Promise<Message> {
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    const optimisticMessage: Message = {
      id: tempId,
      tempId,
      fromMe: true,
      text: content.text || (content.file ? content.file.name : undefined),
      imageUrl: content.imageUrl,
      attachments: content.mediaUrl ? [{type: content.mediaType, payload: {url: content.mediaUrl}}] : undefined,
      createdAt: new Date().toISOString(),
      status: 'sending',
      senderKind: (options.senderKind as any) || 'human',
      origin: (options.origin as any) || 'web',
      botName: options.botName || undefined
    };
    
    // Emit optimistic update
    chatEvents.emit('message:optimistic', { threadId, message: optimisticMessage });

    if (isDemoModeActive()) {
      let sentMessage!: Message;
      const state = demoStore.mutate(data => {
        sentMessage = { ...optimisticMessage, id: `demo-message-${demoStore.nextId(data)}`, status: 'sent' };
        (data.messages[threadId] ||= []).push(sentMessage);
        const thread = data.threads.find(item => item.id === threadId);
        if (thread) { thread.lastMessage = content.text || 'Imagen'; thread.updated_time = sentMessage.createdAt; }
      });
      cache.set(`${MESSAGES_CACHE_PREFIX}${threadId}`, state.messages[threadId]);
      cache.set(THREADS_CACHE_KEY, reorderThreadsPinnedFirst(state.threads));
      chatEvents.emit('message:sent', { threadId, tempId, message: sentMessage });
      chatEvents.emit('thread:typing', { threadId, typing: true });
      window.setTimeout(async () => {
        if (!isDemoModeActive()) return;
        try {
          await this.generateDemoAIMessage(threadId, 'customer', true);
        } catch (error) {
          console.error('Demo customer AI generation failed:', error);
        } finally {
          chatEvents.emit('thread:typing', { threadId, typing: false });
        }
      }, 700);
      return sentMessage;
    }
    
    try {
      const form = new FormData();
      if (content.file) form.append('file', content.file);
      const { data } = content.file
        ? await fastapi.post(`/chat/threads/${encodeURIComponent(threadId)}/media`, form, { headers: { 'Content-Type': undefined }, timeout: 180000 })
        : await fastapi.post(`/chat/threads/${encodeURIComponent(threadId)}/messages`, { ...content, ...options });
      
      const responseData = data.data || data;
      const sentMessage = { ...responseData, status: 'sent' as const };
      
      // Update cache
      const cacheKey = `${MESSAGES_CACHE_PREFIX}${threadId}`;
      const cached = cache.get(cacheKey);
      if (cached) {
        const updated = cached.filter((m: Message) => m.tempId !== tempId);
        updated.push(sentMessage);
        cache.set(cacheKey, updated);
      }
      
      // Emit success
      chatEvents.emit('message:sent', { threadId, tempId, message: sentMessage });
      
      return sentMessage;
    } catch (error) {
      // Emit error
      chatEvents.emit('message:error', { threadId, tempId, error });
      throw error;
    }
  }
  
  // Toggle pin
  async togglePin(threadId: string, isPinned: boolean): Promise<void> {
    if (isDemoModeActive()) {
      const data = demoStore.mutate(state => {
        const pinned = state.pinnedIds.includes(threadId);
        state.pinnedIds = pinned ? state.pinnedIds.filter(id => id !== threadId) : [...state.pinnedIds, threadId];
      });
      const pinnedOrder = Object.fromEntries(data.pinnedIds.map((id, index) => [id, index]));
      cache.set(PINNED_CACHE_KEY, { pinnedIds: data.pinnedIds, pinnedOrder });
      cache.set(THREADS_CACHE_KEY, reorderThreadsPinnedFirst(data.threads));
      chatEvents.emit('pinned:updated', { threadId, isPinned: !isPinned, pinnedOrder });
      return;
    }
    try {
      const ck = getCanonicalComercioKey();
      if (isPinned) {
        await api.post('/v1/chat/pinned-threads', { thread_id: threadId, comercio_key: ck || undefined });
      } else {
        await api.post('/v1/chat/pinned-threads', { thread_id: threadId, comercio_key: ck || undefined });
      }

      // Optimistically update local pinned cache and threads ordering; SSE will confirm
      try {
        const state = cache.get(PINNED_CACHE_KEY) || { pinnedIds: [], pinnedOrder: {} };
        const pinnedIds: string[] = Array.isArray(state.pinnedIds) ? [...state.pinnedIds] : [];
        const pinnedOrder: Record<string, number> =
          state && typeof state.pinnedOrder === 'object' && state.pinnedOrder ? { ...state.pinnedOrder } : {};
        if (isPinned) {
          const idx = pinnedIds.indexOf(threadId);
          if (idx >= 0) pinnedIds.splice(idx, 1);
          delete pinnedOrder[threadId];
        } else {
          if (!pinnedIds.includes(threadId)) pinnedIds.push(threadId);
          if (typeof pinnedOrder[threadId] === 'undefined') pinnedOrder[threadId] = 0;
        }
        cache.set(PINNED_CACHE_KEY, { pinnedIds, pinnedOrder }, 10 * 60 * 1000);

        const list = cache.get(THREADS_CACHE_KEY);
        if (Array.isArray(list)) {
          const pinnedSet = new Set(pinnedIds);
          const sortByTime = (a: any, b: any) =>
            String(b.updated_time || '').localeCompare(String(a.updated_time || ''));
          const pinnedList = list
            .filter((t: any) => pinnedSet.has(t.id))
            .sort(
              (a: any, b: any) =>
                (Number(pinnedOrder[a.id] || 0) - Number(pinnedOrder[b.id] || 0)) || sortByTime(a, b)
            );
          const unpinnedList = list
            .filter((t: any) => !pinnedSet.has(t.id))
            .sort((a: any, b: any) => {
              const au = Number(((a as any).unreadCount ?? ((a as any).unread ? 1 : 0)) > 0);
              const bu = Number(((b as any).unreadCount ?? ((b as any).unread ? 1 : 0)) > 0);
              return (bu - au) || sortByTime(a, b);
            });
          const sorted = [...pinnedList, ...unpinnedList];
          cache.set(THREADS_CACHE_KEY, sorted);
        }

        // Emit event so components update their local state (badges/buttons)
        chatEvents.emit('pinned:updated', { threadId, isPinned: !isPinned, pinnedOrder });
      } catch {}

      // No forced refresh; rely on SSE + optimistic local cache
    } catch (error) {
      console.error('Error toggling pin:', error);
      throw error;
    }
  }
  
  // Mark as read
  async markAsRead(threadId: string): Promise<void> {
    // Emit local event
    chatEvents.emit('thread:read', threadId);

    if (isDemoModeActive()) {
      const data = demoStore.mutate(state => {
        const thread = state.threads.find(item => item.id === threadId);
        if (thread) { thread.unreadCount = 0; thread.unreadClientCount = 0; thread.unreadBotCount = 0; thread.unread = false; }
      });
      cache.set(THREADS_CACHE_KEY, reorderThreadsPinnedFirst(data.threads));
      this.unreadCount = data.threads.reduce((sum, thread) => sum + Number(thread.unreadCount || 0), 0);
      chatEvents.emit('unread:count', this.unreadCount);
      chatEvents.emit('threads:updated', data.threads);
      return;
    }

    // Try to persist read state in backend (records read_source='app' and updates chat_sessions markers)
    try {
      const ck = getCanonicalComercioKey();
      const q = ck ? `?comercio_key=${encodeURIComponent(ck)}` : '';
      await api.post(`/v1/chat/threads/${encodeURIComponent(threadId)}/read${q}`, {});
    } catch (e) {
      console.debug('Backend read endpoint error or unavailable, applying local cache-only update');
    } finally {
      // Update cache so UI refreshes immediately — clear both client and bot unseen counters
      const cached = cache.get(THREADS_CACHE_KEY);
      if (Array.isArray(cached)) {
        const list = cached.map((t: any) => {
          if (String(t.id) === String(threadId)) {
            return {
              ...t,
              unreadClientCount: 0,
              unreadBotCount: 0,
              unreadCount: 0,
              unread: false
            };
          }
          return t;
        });
        cache.set(THREADS_CACHE_KEY, reorderThreadsPinnedFirst(list));
        // Navbar total: sum client unseen only
        const total = list.reduce(
          (acc: number, t: any) => acc + Number((t as any).unreadClientCount || 0),
          0
        );
        chatEvents.emit('unread:count', total);
        // Also emit an update so any patchers can re-render
        chatEvents.emit('threads:updated', { summary: { id: threadId, unreadCount: 0, unread: false } });
      }
    }
  }
  
  // Update unread count
  private updateUnreadCount(threads: Thread[]): void {
    try {
      // Navbar: show total unseen (client + bot) from refresh
      const total = threads.reduce((acc, t: any) => acc + Number((t as any).unreadCount || 0), 0);
      this.unreadCount = total;
      chatEvents.emit('unread:count', total);
      
      // Update document title
      const baseTitle = document.title.replace(/^\(\d+\) /, '');
      document.title = total > 0 ? `(${total}) ${baseTitle}` : baseTitle;
    } catch (error) {
      console.error('Error updating unread count:', error);
    }
  }
  
  // Get unread count
  getUnreadCount(): number {
    return this.unreadCount;
  }
// Polling fallback: refresh threads periodically if SSE is unavailable
private startPolling(): void {
  try {
    if ((this as any).pollTimer) return;
    // Refresh visible operators when SSE is unavailable.
    (this as any).pollTimer = setInterval(() => {
      try {
        if (!document.hidden && !sse.isConnected()) {
          chatEvents.emit('threads:updated', { reason: 'poll' });
        }
      } catch {}
    }, 60000);
    console.debug('[ChatPolling] started (60s interval, only when visible and SSE disconnected)');
  } catch {}
}

private stopPolling(): void {
  try {
    const t = (this as any).pollTimer;
    if (t) {
      clearInterval(t);
      (this as any).pollTimer = null;
      console.debug('[ChatPolling] stopped');
    }
  } catch {}
}
  
  // Event subscription helpers
  on(event: string, handler: (...args: any[]) => void): void {
    chatEvents.on(event, handler);
  }
  
  off(event: string, handler: (...args: any[]) => void): void {
    chatEvents.off(event, handler);
  }
  
  once(event: string, handler: (...args: any[]) => void): void {
    chatEvents.once(event, handler);
  }
  
  // Settings
  setSoundEnabled(enabled: boolean): void {
    notifications.setSoundEnabled(enabled);
  }
  
  isSoundEnabled(): boolean {
    return notifications.isSoundEnabled();
  }
  
  isSSEConnected(): boolean {
    return sse.isConnected();
  }

  // Track currently selected thread for SSE optimistic decisions
  setCurrentThreadId(id: string | null): void {
    this.currentSelectedThreadId = id ? String(id) : null;
  }
  getCurrentSelectedThreadId(): string | null {
    return this.currentSelectedThreadId;
  }

  // Expose current pinned snapshot (for UI boot)
  getPinnedSnapshot(): { pinnedIds: string[]; pinnedOrder: Record<string, number> } {
    if (isDemoModeActive()) {
      const pinnedIds = demoStore.mutate(() => {}).pinnedIds;
      return { pinnedIds, pinnedOrder: Object.fromEntries(pinnedIds.map((id, index) => [id, index])) };
    }
    const state = cache.get(PINNED_CACHE_KEY) || {};
    const pinnedIds = Array.isArray((state as any).pinnedIds) ? (state as any).pinnedIds : [];
    const pinnedOrder =
      (state as any).pinnedOrder && typeof (state as any).pinnedOrder === 'object' ? (state as any).pinnedOrder : {};
    return { pinnedIds, pinnedOrder };
  }
  
  reconnectSSE(): void {
    sse.reconnect();
  }
  
  // Cache/dirty helpers
  hasCachedMessages(threadId: string): boolean {
    const cacheKey = `${MESSAGES_CACHE_PREFIX}${threadId}`;
    return cache.has(cacheKey);
  }
  // Synchronous cached read with zero network — for instant render on selection
  getMessagesCached(threadId: string): Message[] {
    const cacheKey = `${MESSAGES_CACHE_PREFIX}${threadId}`;
    const arr = cache.get(cacheKey);
    return Array.isArray(arr) ? arr : [];
  }
  getMessagesCachedPage(threadId: string): MessagesPage {
    const messages = this.getMessagesCached(threadId);
    const meta = cache.get(`${MESSAGES_PAGE_META_PREFIX}${threadId}`) as
      | Omit<MessagesPage, 'messages'>
      | null;
    return {
      messages,
      nextCursor: meta?.nextCursor || null,
      hasMore: !!meta?.hasMore,
    };
  }
  willFetchMessages(threadId: string): boolean {
    // If thread is marked dirty or we have no cache, we must fetch
    if (this.isThreadDirty(threadId) || !this.hasCachedMessages(threadId)) return true;
    // Staleness policy: if we recently fetched and push is connected, avoid refetch on selection
    const last = this.lastFetchedAt.get(threadId) || 0;
    const age = Date.now() - last;
    const sseConnected = this.isSSEConnected();
    // Freshness windows (ms)
    const FRESH_TTL_CONNECTED = 20000;   // 20s when SSE is connected
    const FRESH_TTL_DISCONNECTED = 10000; // 10s when SSE is disconnected
    const ttl = sseConnected ? FRESH_TTL_CONNECTED : FRESH_TTL_DISCONNECTED;
    return !(last > 0 && age <= ttl);
  }
  
  // Dirty-thread helpers
  isThreadDirty(threadId: string): boolean {
    return this.dirtyThreads.has(threadId);
  }
  clearThreadDirty(threadId: string): void {
    this.dirtyThreads.delete(threadId);
  }

  // Tags & Folders API
  async listTagsWithFolders(forceRefresh = false): Promise<{ tags: Tag[]; folders: TagFolder[] }> {
    if (isDemoModeActive()) return demoStore.tags();
    if (!forceRefresh && cache.has(TAGS_CACHE_KEY) && cache.has(TAG_FOLDERS_CACHE_KEY)) {
      const tags = cache.get(TAGS_CACHE_KEY) || [];
      const folders = cache.get(TAG_FOLDERS_CACHE_KEY) || [];
      return { tags: Array.isArray(tags) ? tags : [], folders: Array.isArray(folders) ? folders : [] };
    }
    return requestQueue.debounce('tags_with_folders', async () => {
      try {
        await waitForAuthReady();
        ensureComercioKeyFromJWT();
        const ck = getCanonicalComercioKey();
const qParts: string[] = ['include_folders=true'];
if (ck) qParts.push(`comercio_key=${encodeURIComponent(ck)}`);
qParts.push(`ts=${Date.now()}`);
const params = `?${qParts.join('&')}`;
const { data } = await api.get(`/chat/tags${params}`);
        const payload: any = data || {};
        const tags: Tag[] = Array.isArray(payload.tags) ? payload.tags : (Array.isArray(payload.data) ? payload.data : []);
        const folders: TagFolder[] = Array.isArray(payload.folders) ? payload.folders : [];
        cache.set(TAGS_CACHE_KEY, tags, 5 * 60 * 1000);
        cache.set(TAG_FOLDERS_CACHE_KEY, folders, 5 * 60 * 1000);
        return { tags, folders };
      } catch (e: any) {
        const status = e?.response?.status || e?.status;
        // Propagate auth errors so UI can handle (e.g., show message or redirect to login)
        if (status === 401 || status === 403) {
          throw e;
        }
        console.error('Error fetching tags with folders:', e);
        const tags = cache.get(TAGS_CACHE_KEY) || [];
        const folders = cache.get(TAG_FOLDERS_CACHE_KEY) || [];
        return { tags: Array.isArray(tags) ? tags : [], folders: Array.isArray(folders) ? folders : [] };
      }
    });
  }

  async listTags(search?: string, includeFolders = false, forceRefresh = false): Promise<Tag[] | { tags: Tag[]; folders: TagFolder[] }> {
    if (isDemoModeActive()) {
      const result = demoStore.tags();
      const tags = search ? result.tags.filter(tag => tag.name.toLowerCase().includes(search.toLowerCase())) : result.tags;
      return includeFolders ? { tags, folders: result.folders } : tags;
    }
    if (!forceRefresh && cache.has(TAGS_CACHE_KEY) && !includeFolders) {
      const tags = cache.get(TAGS_CACHE_KEY) || [];
      return Array.isArray(tags) ? tags : [];
    }
    return requestQueue.debounce(`tags_${String(search || '').toLowerCase()}_${includeFolders ? 'withF' : 'noF'}`, async () => {
      const ck = getCanonicalComercioKey();
      const q: string[] = [];
      if (search && search.trim() !== '') q.push(`search=${encodeURIComponent(search.trim())}`);
      if (includeFolders) q.push('include_folders=true');
      if (ck) q.push(`comercio_key=${encodeURIComponent(ck)}`);
q.push(`ts=${Date.now()}`);
const query = q.length ? `?${q.join('&')}` : '';
const { data } = await api.get(`/chat/tags${query}`);
      const payload: any = data || {};
      const maybeCombined = Array.isArray(payload.tags) || Array.isArray(payload.folders);
      const tags: Tag[] = maybeCombined ? (payload.tags || []) : (Array.isArray(payload.data) ? payload.data : []);
      if (!includeFolders) cache.set(TAGS_CACHE_KEY, tags, 5 * 60 * 1000);
      if (includeFolders) {
        const folders: TagFolder[] = payload.folders || [];
        cache.set(TAGS_CACHE_KEY, tags, 5 * 60 * 1000);
        cache.set(TAG_FOLDERS_CACHE_KEY, folders, 5 * 60 * 1000);
        return { tags, folders };
      }
      return tags;
    });
  }

  async listFolders(forceRefresh = false): Promise<TagFolder[]> {
    if (isDemoModeActive()) return demoStore.tags().folders;
    if (!forceRefresh && cache.has(TAG_FOLDERS_CACHE_KEY)) {
      const folders = cache.get(TAG_FOLDERS_CACHE_KEY) || [];
      return Array.isArray(folders) ? folders : [];
    }
    return requestQueue.debounce('tag_folders', async () => {
      const ck = getCanonicalComercioKey();
      const params = `${ck ? `?comercio_key=${encodeURIComponent(ck)}` : ''}${ck ? '&' : '?'}ts=${Date.now()}`;
      const { data } = await api.get(`/chat/tag-folders${params}`);
      const payload: any = data || {};
      const folders: TagFolder[] = Array.isArray(payload.folders) ? payload.folders : (Array.isArray(payload.data) ? payload.data : []);
      cache.set(TAG_FOLDERS_CACHE_KEY, folders, 5 * 60 * 1000);
      return folders;
    });
  }

  async createTag(input: { name: string; color?: string | null; folder_id?: number | null }): Promise<Tag | null> {
    if (isDemoModeActive()) {
      let tag!: Tag;
      demoStore.mutate(data => { tag = { id: demoStore.nextId(data), name: input.name, color: input.color, folder_id: input.folder_id }; data.tags.push(tag); });
      return tag;
    }
    try {
      const ck = getCanonicalComercioKey();
      const body: any = { ...input, comercio_key: ck || undefined };
      const { data } = await api.post('/chat/tags', body);
      const payload: any = data || {};
      const tag: Tag | null = payload.tag || payload.data || null;
      cache.invalidate(TAGS_CACHE_KEY);
      return tag;
    } catch (e: any) {
      if (e?.response?.status === 409) {
        console.warn('Tag already exists:', input.name);
        await this.listTagsWithFolders(true);
        return null;
      }
      console.error('Error creating tag:', e);
      throw e;
    }
  }

  async updateTag(id: number, patch: { name?: string; color?: string | null; folder_id?: number | null }): Promise<void> {
    if (isDemoModeActive()) { demoStore.mutate(data => { const tag = data.tags.find(item => item.id === id); if (tag) Object.assign(tag, patch); }); return; }
    const ck = getCanonicalComercioKey();
    await api.put(`/chat/tags/${id}${ck ? `?comercio_key=${encodeURIComponent(ck)}` : ''}`, patch);
    cache.invalidate(TAGS_CACHE_KEY);
  }

  async deleteTag(id: number): Promise<void> {
    if (isDemoModeActive()) { demoStore.mutate(data => { data.tags = data.tags.filter(tag => tag.id !== id); for (const key of Object.keys(data.threadTags)) data.threadTags[key] = data.threadTags[key].filter(tagId => tagId !== id); }); return; }
    const ck = getCanonicalComercioKey();
    await api.delete(`/chat/tags/${id}${ck ? `?comercio_key=${encodeURIComponent(ck)}` : ''}`);
    cache.invalidate(TAGS_CACHE_KEY);
  }

  async createFolder(input: { name: string; parent_id?: number | null }): Promise<TagFolder | null> {
    if (isDemoModeActive()) {
      let folder!: TagFolder;
      demoStore.mutate(data => { folder = { id: demoStore.nextId(data), name: input.name, parent_id: input.parent_id }; data.folders.push(folder); });
      return folder;
    }
    try {
      const ck = getCanonicalComercioKey();
      const body: any = { ...input, comercio_key: ck || undefined };
      const { data } = await api.post('/chat/tag-folders', body);
      const payload: any = data || {};
      const folder: TagFolder | null = payload.folder || payload.data || null;
      cache.invalidate(TAG_FOLDERS_CACHE_KEY);
      return folder;
    } catch (e: any) {
      if (e?.response?.status === 409) {
        console.warn('Folder already exists:', input.name);
        await this.listFolders(true);
        return null;
      }
      console.error('Error creating folder:', e);
      throw e;
    }
  }

  async updateFolder(id: number, patch: { name?: string; parent_id?: number | null }): Promise<void> {
    if (isDemoModeActive()) { demoStore.mutate(data => { const folder = data.folders.find(item => item.id === id); if (folder) Object.assign(folder, patch); }); return; }
    const ck = getCanonicalComercioKey();
    await api.put(`/chat/tag-folders/${id}${ck ? `?comercio_key=${encodeURIComponent(ck)}` : ''}`, patch);
    cache.invalidate(TAG_FOLDERS_CACHE_KEY);
  }

  async deleteFolder(id: number, opts: { move_to_id?: number | null } = {}): Promise<void> {
    if (isDemoModeActive()) { demoStore.mutate(data => { data.folders = data.folders.filter(folder => folder.id !== id); data.tags.forEach(tag => { if (tag.folder_id === id) tag.folder_id = opts.move_to_id ?? null; }); }); return; }
    const ck = getCanonicalComercioKey();
    const q: string[] = [];
    if (ck) q.push(`comercio_key=${encodeURIComponent(ck)}`);
    if (opts.move_to_id !== undefined) q.push(`move_to_id=${encodeURIComponent(String(opts.move_to_id ?? ''))}`);
    const query = q.length ? `?${q.join('&')}` : '';
    await api.delete(`/chat/tag-folders/${id}${query}`);
    cache.invalidate(TAG_FOLDERS_CACHE_KEY);
    cache.invalidate(TAGS_CACHE_KEY);
  }

  async getThreadTags(threadId: string, forceRefresh = false): Promise<Tag[]> {
    if (isDemoModeActive()) return demoStore.threadTags(threadId);
    const cacheKey = `${THREAD_TAGS_PREFIX}${threadId}`;
    if (!forceRefresh && cache.has(cacheKey)) {
      const arr = cache.get(cacheKey) || [];
      return Array.isArray(arr) ? arr : [];
    }
    return requestQueue.debounce(`thread_tags_${threadId}`, async () => {
      const ck = getCanonicalComercioKey();
      const parts: string[] = [];
      if (ck) parts.push(`comercio_key=${encodeURIComponent(ck)}`);
      parts.push(`ts=${Date.now()}`);
      const qs = parts.length ? `?${parts.join('&')}` : '';
      const { data } = await api.get(`/chat/chat-sessions/${encodeURIComponent(threadId)}/tags${qs}`);
      const payload: any = data || {};
      const tags: Tag[] = Array.isArray(payload.tags) ? payload.tags : (Array.isArray(payload.data) ? payload.data : []);
      cache.set(cacheKey, tags, 5 * 60 * 1000);
      return tags;
    });
  }

  async getThreadTagsBulk(threadIds: string[], overrideCk?: string): Promise<Record<string, Tag[]>> {
    if (!Array.isArray(threadIds) || threadIds.length === 0) return {};
    if (isDemoModeActive()) return Object.fromEntries(threadIds.map(id => [id, demoStore.threadTags(id)]));
    const ck = overrideCk || getCanonicalComercioKey();
    const parts: string[] = [];
    for (const id of threadIds) parts.push(`thread_ids[]=${encodeURIComponent(id)}`);
    if (ck) parts.push(`comercio_key=${encodeURIComponent(ck)}`);
    parts.push(`ts=${Date.now()}`);
    const query = `?${parts.join('&')}`;
    const { data } = await api.get(`/chat/thread-tags${query}`);
    const payload: any = data || {};

    // Normalize diverse backend shapes to a consistent mapping
    let mapping: Record<string, Tag[]> = {};
    if (payload && typeof payload === 'object') {
      if (payload.mapping && typeof payload.mapping === 'object' && !Array.isArray(payload.mapping)) {
        const rawMapping = payload.mapping as Record<string, any[]>;
        Object.keys(rawMapping).forEach(tid => {
          mapping[tid] = rawMapping[tid].map((t: any) => ({
            ...t,
            id: t.id ?? t.tag_id
          }));
        });
      } else if (payload.map && typeof payload.map === 'object' && !Array.isArray(payload.map)) {
        mapping = payload.map as Record<string, Tag[]>;
      } else if (Array.isArray(payload.data)) {
        // Convert array items to mapping: supports { thread_id, tags } or { id, tags/data }
        for (const item of payload.data) {
          const tid = String(item?.thread_id ?? item?.id ?? '');
          if (!tid) continue;
          const tagsArr: Tag[] = Array.isArray(item?.tags)
            ? item.tags
            : (Array.isArray(item?.data) ? item.data : []);
          mapping[tid] = Array.isArray(tagsArr) ? tagsArr : [];
        }
      } else if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
        mapping = payload.data as Record<string, Tag[]>;
      }
    }

    // Prime per-thread caches
    Object.keys(mapping || {}).forEach(tid => {
      if (Array.isArray(mapping[tid])) cache.set(`${THREAD_TAGS_PREFIX}${tid}`, mapping[tid], 5 * 60 * 1000);
    });

    return mapping;
  }

  async addTagToThread(threadId: string, payload: { tag_id?: number; tag_name?: string; color?: string | null; folder_id?: number | null }): Promise<void> {
    if (isDemoModeActive()) {
      demoStore.mutate(data => {
        let id = payload.tag_id;
        if (!id && payload.tag_name) { id = demoStore.nextId(data); data.tags.push({ id, name: payload.tag_name, color: payload.color, folder_id: payload.folder_id }); }
        if (id && !(data.threadTags[threadId] ||= []).includes(id)) data.threadTags[threadId].push(id);
      });
      chatEvents.emit('thread:tags', { threadId, tags: demoStore.threadTags(threadId) });
      return;
    }
    const ck = getCanonicalComercioKey();
    const body: any = { ...payload, comercio_key: ck || undefined };
    await api.post(`/chat/chat-sessions/${encodeURIComponent(threadId)}/tags`, body);
    // Refresh mapping cache and emit a local event for immediate UI update; SSE will also arrive
    try {
      const tags = await this.getThreadTags(threadId, true);
      (chatEvents as any).emit?.('thread:tags', { threadId, tags });
    } catch {}
  }

  async removeTagFromThread(threadId: string, tagId: number): Promise<void> {
    if (isDemoModeActive()) {
      demoStore.mutate(data => { data.threadTags[threadId] = (data.threadTags[threadId] || []).filter(id => id !== tagId); });
      chatEvents.emit('thread:tags', { threadId, tags: demoStore.threadTags(threadId) });
      return;
    }
    const ck = getCanonicalComercioKey();
    await api.delete(`/chat/chat-sessions/${encodeURIComponent(threadId)}/tags/${tagId}${ck ? `?comercio_key=${encodeURIComponent(ck)}` : ''}`);
    try {
      const tags = await this.getThreadTags(threadId, true);
      (chatEvents as any).emit?.('thread:tags', { threadId, tags });
    } catch {}
  }

  // Filtering helpers: combine text + AND tags + platform while preserving SSOT ordering
  async filterThreads(opts: { text?: string; platform?: string; tagIds?: number[] } = {}): Promise<Thread[]> {
    const base = await this.getThreads();
    let filtered = Array.isArray(base) ? [...base] : [];

    // Platform filter: default instagram; for now only instagram is supported. Non-instagram => empty.
    const platform = (opts.platform || 'instagram').toLowerCase();
    if (platform !== 'instagram') {
      return [];
    }

    // Text filter (case/diacritic-insensitive) on name, username, lastMessage
    const text = (opts.text || '').trim();
    if (text) {
      const normalize = (s: any) =>
        String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const needle = normalize(text);
      filtered = filtered.filter(t => {
        const name = normalize((t as any).name);
        const username = normalize((t as any).username || '');
        const lastMessage = normalize((t as any).lastMessage || '');
        return name.includes(needle) || username.includes(needle) || lastMessage.includes(needle);
      });
    }

    // AND-semantics tags filter
    const tagIds = (opts.tagIds || []).map(Number).filter(n => Number.isFinite(n));
    if (tagIds.length > 0 && filtered.length > 0) {
      const map = await this.getThreadTagsBulk(filtered.map(t => t.id));
      const required = new Set<number>(tagIds);
      filtered = filtered.filter(t => {
        const tags: Tag[] = (map && (map as any)[t.id]) || [];
        if (!Array.isArray(tags) || tags.length === 0) return false;
        const have = new Set<number>(tags.map(tag => Number((tag as any).id)));
        for (const r of required) if (!have.has(r)) return false;
        return true;
      });
    }

    return filtered;
  }

  async getThreadIdsWithAllTags(tagIds: number[], threadIds?: string[], overrideCk?: string): Promise<string[]> {
    const required = (tagIds || []).map(Number).filter(n => Number.isFinite(n));
    // Define scope explicitly to keep SSOT ordering and avoid full-table scans
    const scopeIds = Array.isArray(threadIds) && threadIds.length > 0
      ? threadIds
      : (await this.getThreads()).map(t => t.id);
  
    if (required.length === 0) {
      return scopeIds;
    }
    if (scopeIds.length === 0) return [];

    if (isDemoModeActive()) {
      return scopeIds.filter(threadId => {
        const ids = new Set(demoStore.threadTags(threadId).map(tag => tag.id));
        return required.every(id => ids.has(id));
      });
    }
  
    // Consistent, server-side AND filtering (no local fallbacks)
    const ck = overrideCk || getCanonicalComercioKey();
    const params: string[] = [];
    for (const id of required) params.push(`tag_ids[]=${encodeURIComponent(String(id))}`);
    if (ck) params.push(`comercio_key=${encodeURIComponent(ck)}`);
    params.push(`ts=${Date.now()}`);
  
    try {
      // Ask backend for all thread ids that match, then we will intersect with scopeIds on the client
      const { data } = await api.get(`/chat/thread-tags/filter?${params.join('&')}`);
      const ids: string[] = Array.isArray(data?.ids) ? data.ids.map(String) : [];
      // Intersect with SSOT scope to keep UI consistent and avoid showing unknown threads
      const scopeSet = new Set(scopeIds.map(String));
      return ids.filter(id => scopeSet.has(String(id)));
    } catch (e) {
      console.error('Error filtering thread ids by tags (server):', e);
      return [];
    }
  }

  async generateDemoAIMessage(
    threadId: string,
    role: 'customer' | 'operator',
    append = role === 'customer'
  ): Promise<string> {
    if (!isDemoModeActive()) throw new Error('Demo AI messages require an active demo session');
    const state = demoStore.mutate(() => {});
    const thread = state.threads.find(item => item.id === threadId);
    if (!thread) throw new Error('Demo thread not found');
    const recent = (state.messages[threadId] || []).slice(-12).filter(message => String(message.text || '').trim());
    const { data: response } = await fastapi.post('/tenants/me/demo/generate-message', {
      role,
      patient_name: thread.name,
      scenario: thread.demo_scenario || 'consulta clínica',
      recent_messages: recent.map(message => ({ from_me: !!message.fromMe, text: String(message.text).slice(0, 600) })),
    });
    const text = String(response?.text || '').trim();
    if (!text) throw new Error('The AI returned an empty demo message');
    if (!append) return text;

    let generated!: Message;
    const updated = demoStore.mutate(data => {
      generated = {
        id: `demo-ai-${demoStore.nextId(data)}`,
        fromMe: role === 'operator',
        text,
        createdAt: new Date().toISOString(),
        senderKind: role === 'operator' ? 'operator' : 'human',
        origin: role === 'operator' ? 'web' : 'instagram',
        status: role === 'operator' ? 'sent' : 'delivered',
      };
      (data.messages[threadId] ||= []).push(generated);
      const target = data.threads.find(item => item.id === threadId);
      if (target) {
        target.lastMessage = text;
        target.updated_time = generated.createdAt;
        if (role === 'customer') {
          target.unreadCount = Number(target.unreadCount || 0) + 1;
          target.unreadClientCount = Number(target.unreadClientCount || 0) + 1;
          target.unread = true;
          target.replyWindowEndsAt = replyWindowEndsAtFromInbound(generated.createdAt);
        }
      }
    });
    cache.set(`${MESSAGES_CACHE_PREFIX}${threadId}`, updated.messages[threadId]);
    cache.set(THREADS_CACHE_KEY, reorderThreadsPinnedFirst(updated.threads));
    chatEvents.emit('message:received', {
      thread_id: threadId,
      preview: text,
      from: role === 'operator' ? 'me' : 'client',
      sender_kind: role === 'operator' ? 'operator' : 'human',
      origin: role === 'operator' ? 'web' : 'instagram',
      at: generated.createdAt,
      message: generated,
    });
    chatEvents.emit('threads:updated', updated.threads);
    return text;
  }

  async draftBotResponse(
    threadId: string,
    opts: { botId?: number | null; channel?: 'instagram' | 'whatsapp' | 'email'; historyLimit?: number } = {}
  ): Promise<{ suggestions: string[]; used_bot?: any; commerce?: any; n8n_raw?: any }> {
    if (isDemoModeActive()) {
      const text = await this.generateDemoAIMessage(threadId, 'operator', false);
      return { suggestions: [text], used_bot: { id: 1, name: 'Asistente Clínica' } };
    }
    try {
      const body: any = {
        thread_id: threadId,
        channel: opts.channel || 'instagram',
        history_limit: opts.historyLimit || 20
      };
      
      if (opts.botId !== undefined && opts.botId !== null) {
        body.bot_id = Number(opts.botId);
      }

      const { data } = await fastapi.post('/chat/draft-bot-response', body);

      return {
        suggestions: data.suggestions || [],
        used_bot: data.used_bot || null
      };
    } catch (error) {
      console.error('Error in draftBotResponse:', error);
      return { suggestions: [] };
    }
  }
}
 
// Export singleton instance
const chatService = new ChatService();
export default chatService;

// Export types and events
export { chatEvents, ChatEventEmitter };
// Keep pinned threads always at the top: utility to reorder cached threads
function reorderThreadsPinnedFirst(list: any[]): any[] {
  try {
    const state: any = (cache as any).get ? (cache as any).get('chat_pinned_cache') : null;
    const pinnedIds: string[] = Array.isArray(state?.pinnedIds) ? state.pinnedIds.map(String) : [];
    const pinnedOrder: Record<string, number> =
      state && state.pinnedOrder && typeof state.pinnedOrder === 'object' ? state.pinnedOrder : {};
    const pinnedSet = new Set<string>(pinnedIds);

    const sortByTime = (a: any, b: any) =>
      String(b.updated_time || '').localeCompare(String(a.updated_time || ''));

    const toNumber = (v: any) => {
      if (v == null) return 0;
      const n = Number(v);
      return isNaN(n) ? (v === true ? 1 : 0) : n;
    };

    const isUnread = (t: any) => toNumber((t as any).unreadCount ?? (t as any).unread);

    const pinnedList = (list || [])
      .filter((t: any) => pinnedSet.has(String(t.id)))
      .sort((a: any, b: any) =>
        (Number(pinnedOrder[a.id] || 0) - Number(pinnedOrder[b.id] || 0)) || sortByTime(a, b)
      );

    const unpinnedList = (list || [])
      .filter((t: any) => !pinnedSet.has(String(t.id)))
      .sort((a: any, b: any) => {
        const au = isUnread(a);
        const bu = isUnread(b);
        const cu = (bu - au);
        if (cu !== 0) return cu;
        return sortByTime(a, b);
      });

    return [...pinnedList, ...unpinnedList];
  } catch {
    // Fallback: unread first, then by time
    const sortByTime = (a: any, b: any) =>
      String(b.updated_time || '').localeCompare(String(a.updated_time || ''));
    const toNumber = (v: any) => {
      if (v == null) return 0;
      const n = Number(v);
      return isNaN(n) ? (v === true ? 1 : 0) : n;
    };
    const isUnread = (t: any) => toNumber((t as any).unreadCount ?? (t as any).unread);
    return (list || []).slice().sort((a: any, b: any) => {
      const au = isUnread(a);
      const bu = isUnread(b);
      const cu = (bu - au);
      if (cu !== 0) return cu;
      return sortByTime(a, b);
    });
  }
}
